/**
 * Hyperlinks with custom text in the message composer.
 *
 * Messages are plain text; linkify.tsx already renders markdown `[text](url)`
 * as a safe link (and unfurls it). These helpers write that markdown for the
 * sender so nobody has to type it by hand:
 *   - Insert link (button / Cmd-Ctrl+K inside the composer): text + URL.
 *   - Paste a URL over selected text: the selection becomes `[selection](url)`
 *     (Slack behaviour).
 */

import { toSafeHref } from './linkify';

/**
 * Normalise what a user typed as a link target. http(s) URLs pass through; a
 * bare domain ("example.org/path", "www.x.com") gets https://; anything with
 * another scheme (javascript:, data:, mailto:, file:) is rejected.
 */
export function normalizeLinkUrl(raw: string | null | undefined): string | null {
  const value = (raw ?? '').trim();
  if (!value || /\s/.test(value)) return null;
  if (/^https?:\/\//i.test(value)) return toSafeHref(value);
  if (value.startsWith('//')) return normalizeLinkUrl(`https:${value}`);
  // A scheme is letters/digits/+.- followed by ':'; "example.com:8080" is a
  // host:port, not a scheme, so only reject when the part before ':' has no dot.
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(value);
  if (scheme && !scheme[1].includes('.')) return null;
  const host = value.split(/[/?#]/)[0].replace(/:\d+$/, '');
  if (!/^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/i.test(host)) return null;
  return toSafeHref(`https://${value}`);
}

/** Link text safe inside `[...]`: no brackets or newlines (they'd end the label). */
export function sanitizeLinkText(text: string): string {
  return text.replace(/[[\]]/g, '').replace(/\s*\n\s*/g, ' ').trim();
}

export function linkMarkdown(text: string, url: string): string {
  const label = sanitizeLinkText(text);
  return label ? `[${label}](${url})` : url;
}

export interface TextEdit {
  value: string;
  /** Caret position after the edit. */
  cursor: number;
}

/** Replace value[start, end) with the link (or insert it at the caret). */
export function insertLink(value: string, start: number, end: number, text: string, url: string): TextEdit {
  const s = Math.max(0, Math.min(start, value.length));
  const e = Math.max(s, Math.min(end, value.length));
  const md = linkMarkdown(text, url);
  return { value: value.slice(0, s) + md + value.slice(e), cursor: s + md.length };
}

/**
 * Paste-over-selection: when text is selected and the clipboard holds a single
 * URL, wrap the selection as `[selection](url)`. Returns null when the paste
 * should proceed normally.
 */
export function wrapSelectionOnPaste(value: string, start: number, end: number, pasted: string): TextEdit | null {
  if (start === end) return null;
  const candidate = pasted.trim();
  if (!candidate || /\s/.test(candidate)) return null;
  if (!/^(https?:\/\/|www\.)/i.test(candidate)) return null;
  const href = normalizeLinkUrl(candidate);
  if (!href) return null;
  const selection = value.slice(Math.min(start, end), Math.max(start, end));
  if (!sanitizeLinkText(selection)) return null;
  return insertLink(value, Math.min(start, end), Math.max(start, end), selection, href);
}

/** Cmd/Ctrl+K (no Shift/Alt) — the composer's Insert-link shortcut. */
export function isInsertLinkShortcut(e: { key: string; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean }): boolean {
  return (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'k';
}

/**
 * Value for `data-claims-shortcuts` on a composer input: GlobalShortcuts skips
 * these keys when the event target is inside such an element, so Cmd/Ctrl+K
 * means "Insert link" in the composer and "Command palette" everywhere else.
 */
export const COMPOSER_CLAIMED_SHORTCUTS = 'mod+k';
